import React, { useState, useEffect } from "react";
import {
  Card,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  MenuItem,
  Select,
  IconButton,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { collection, addDoc, doc, updateDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { firestore, auth } from "firebaseConfig";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

function DividasPage() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [tabValue, setTabValue] = useState(0);
  const [dividas, setDividas] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editActiveStep, setEditActiveStep] = useState(0);
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState("");
  const [valorTotalNum, setValorTotalNum] = useState("");
  const [parcelas, setParcelas] = useState("");
  const [valorParcelaNum, setValorParcelaNum] = useState("");
  const [vencimentoInicial, setVencimentoInicial] = useState("");
  const [taxaJuros, setTaxaJuros] = useState("");
  const [situacao, setSituacao] = useState("Em andamento");
  const [observacoes, setObservacoes] = useState("");
  const [selectedDividaId, setSelectedDividaId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const steps = ["Informações Básicas", "Detalhes Financeiros", "Confirmação"];

  // Função para formatar valores em moeda BR
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return "R$ 0,00";
    return `R$ ${parseFloat(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Função para lidar com mudanças nos campos de moeda
  const handleCurrencyInputChange = (e, setValue) => {
    const rawValue = e.target.value.replace(/[^\d]/g, "");
    if (rawValue === "") {
      setValue("");
    } else {
      const numericValue = parseFloat(rawValue) / 100 || 0;
      setValue(numericValue.toString());
    }
  };

  // Função para calcular amortização (Tabela Price)
  const calculateAmortization = (valorTotal, taxaJurosMensal, parcelas, valorPago = 0) => {
    if (!valorTotal || !taxaJurosMensal || !parcelas) return { jurosTotal: 0, parcelasRestantes: 0, valorParcela: 0 };

    const taxaDecimal = parseFloat(taxaJurosMensal) / 100;
    const valorTotalNum = parseFloat(valorTotal) - (valorPago || 0);

    const valorParcela =
      (valorTotalNum * (taxaDecimal * Math.pow(1 + taxaDecimal, parcelas))) / (Math.pow(1 + taxaDecimal, parcelas) - 1);
    const totalPago = valorParcela * parcelas;
    const jurosTotal = totalPago - valorTotalNum;
    const saldoDevedor = valorTotalNum;
    const parcelasRestantes = Math.ceil(
      Math.log(valorParcela / (valorParcela - saldoDevedor * taxaDecimal)) / Math.log(1 + taxaDecimal)
    );

    return {
      jurosTotal: jurosTotal > 0 ? jurosTotal : 0,
      parcelasRestantes: parcelasRestantes > 0 ? parcelasRestantes : 0,
      valorParcela: valorParcela > 0 ? valorParcela : 0,
    };
  };

  // Função para comparar arrays de dívidas e evitar atualizações desnecessárias
  const areDividasEqual = (oldDividas, newDividas) => {
    if (oldDividas.length !== newDividas.length) return false;
    return oldDividas.every((oldDivida, index) => {
      const newDivida = newDividas[index];
      return (
        oldDivida.id === newDivida.id &&
        oldDivida.descricao === newDivida.descricao &&
        oldDivida.tipo === newDivida.tipo &&
        oldDivida.valorTotal === newDivida.valorTotal &&
        oldDivida.jurosTotalEstimado === newDivida.jurosTotalEstimado &&
        oldDivida.parcelas === newDivida.parcelas &&
        oldDivida.parcelasPagas === newDivida.parcelasPagas &&
        oldDivida.vencimentoInicial === newDivida.vencimentoInicial &&
        oldDivida.situacao === newDivida.situacao
      );
    });
  };

  // Carregar dívidas do Firestore em tempo real
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setError("Usuário não autenticado.");
      setIsLoading(false);
      return;
    }

    const dividasRef = collection(firestore, `usuarios/${user.uid}/dividas`);
    const unsubscribe = onSnapshot(
      dividasRef,
      (snapshot) => {
        const dividasList = snapshot.docs.map((doc) => {
          const data = doc.data();
          const { jurosTotal, valorParcela } = calculateAmortization(
            data.valorTotal || 0,
            data.taxaJuros || 0,
            data.parcelas || 1,
            data.valorPago || 0
          );
          return {
            id: doc.id,
            ...data,
            jurosTotalEstimado: jurosTotal,
            valorParcela,
            parcelasRestantes: data.parcelas ? Math.max(0, data.parcelas - (data.parcelasPagas || 0)) : 0,
          };
        });

        // Atualizar o estado apenas se os dados forem diferentes
        setDividas((prevDividas) => {
          if (!areDividasEqual(prevDividas, dividasList)) {
            console.log("Atualizando dívidas:", dividasList); // Log para depuração
            return dividasList;
          }
          return prevDividas;
        });

        setIsLoading(false);
      },
      (error) => {
        console.error("Erro ao carregar dívidas:", error);
        setError("Erro ao carregar dívidas: " + error.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleCadastrarDivida = async () => {
    if (!descricao || !tipo || !valorTotalNum || !parcelas || !vencimentoInicial) {
      setError("Preencha todos os campos obrigatórios.");
      return;
    }

    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Usuário não autenticado.");
        setIsLoading(false);
        return;
      }

      const { jurosTotal, valorParcela } = calculateAmortization(
        parseFloat(valorTotalNum),
        parseFloat(taxaJuros) || 0,
        parseInt(parcelas)
      );

      const novaDivida = {
        descricao,
        tipo,
        valorTotal: parseFloat(valorTotalNum) || 0,
        parcelas: parseInt(parcelas) || 1,
        valorParcela: valorParcela || parseFloat(valorParcelaNum) || 0,
        vencimentoInicial: new Date(vencimentoInicial).toISOString(),
        taxaJuros: parseFloat(taxaJuros) || 0,
        situacao,
        observacoes: observacoes || "",
        valorPago: 0,
        parcelasPagas: 0,
        parcelasRestantes: parseInt(parcelas) || 1,
        jurosTotalEstimado: jurosTotal,
        dataCriacao: new Date().toISOString(),
      };

      await addDoc(collection(firestore, `usuarios/${user.uid}/dividas`), novaDivida);

      setOpenModal(false);
      setActiveStep(0);
      setDescricao("");
      setTipo("");
      setValorTotalNum("");
      setParcelas("");
      setValorParcelaNum("");
      setVencimentoInicial("");
      setTaxaJuros("");
      setSituacao("Em andamento");
      setObservacoes("");
      setError("Dívida cadastrada com sucesso!");
      setTimeout(() => setError(""), 3000);
    } catch (error) {
      setError("Erro ao cadastrar dívida: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditarDivida = async (id, updatedDivida) => {
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user || !id) {
        setError("Usuário não autenticado ou ID inválido.");
        return;
      }
      await updateDoc(doc(firestore, `usuarios/${user.uid}/dividas`, id), updatedDivida);
      setOpenEditModal(false);
      setEditActiveStep(0);
      setError("Dívida atualizada com sucesso!");
      setTimeout(() => setError(""), 3000);
    } catch (error) {
      setError("Erro ao atualizar dívida: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExcluirDivida = async (id) => {
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user || !id) {
        setError("Usuário não autenticado ou ID inválido.");
        return;
      }
      await deleteDoc(doc(firestore, `usuarios/${user.uid}/dividas`, id));
      setOpenDeleteConfirm(false);
      setError("Dívida excluída com sucesso!");
      setTimeout(() => setError(""), 3000);
    } catch (error) {
      setError("Erro ao excluir dívida: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Nome da Dívida"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              fullWidth
              required
            />
            <Select value={tipo} onChange={(e) => setTipo(e.target.value)} fullWidth displayEmpty required>
              <MenuItem value="">Selecione o tipo</MenuItem>
              <MenuItem value="Cartão de Crédito">Cartão de Crédito</MenuItem>
              <MenuItem value="Financiamento">Financiamento</MenuItem>
              <MenuItem value="Empréstimo">Empréstimo</MenuItem>
              <MenuItem value="Outros">Outros</MenuItem>
            </Select>
            <TextField
              label="Valor Total (R$)"
              value={formatCurrency(valorTotalNum)}
              onChange={(e) => handleCurrencyInputChange(e, setValorTotalNum)}
              fullWidth
              required
              placeholder="R$ 0,00"
              InputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
            <TextField
              label="Número de Parcelas"
              value={parcelas}
              onChange={(e) => setParcelas(e.target.value)}
              fullWidth
              required
              type="number"
            />
          </MDBox>
        );
      case 1:
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Data de Vencimento da Primeira Parcela"
              type="date"
              value={vencimentoInicial}
              onChange={(e) => setVencimentoInicial(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { height: "56px" } }}
            />
            <TextField
              label="Juros Mensal (%)"
              value={taxaJuros}
              onChange={(e) => setTaxaJuros(e.target.value)}
              fullWidth
              type="number"
              InputProps={{ inputProps: { step: 0.1, min: 0 } }}
            />
            <Select value={situacao} onChange={(e) => setSituacao(e.target.value)} fullWidth required>
              <MenuItem value="Em andamento">Em andamento</MenuItem>
              <MenuItem value="Quitada">Quitada</MenuItem>
            </Select>
            <TextField
              label="Observações"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </MDBox>
        );
      case 2:
        const valorTotalNumParsed = parseFloat(valorTotalNum) || 0;
        const taxaJurosNum = parseFloat(taxaJuros) || 0;
        const parcelasNum = parseInt(parcelas) || 1;
        const { jurosTotal, valorParcela } = calculateAmortization(valorTotalNumParsed, taxaJurosNum, parcelasNum);

        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography variant="h6">Revise os Dados:</MDTypography>
            <MDTypography>Nome: {descricao || "Não informada"}</MDTypography>
            <MDTypography>Tipo: {tipo || "Não informado"}</MDTypography>
            <MDTypography>Valor Total: {formatCurrency(valorTotalNumParsed)}</MDTypography>
            <MDTypography>Número de Parcelas: {parcelasNum}</MDTypography>
            <MDTypography>Valor da Parcela: {formatCurrency(valorParcela)}</MDTypography>
            <MDTypography>
              Data de Vencimento:{" "}
              {vencimentoInicial ? new Date(vencimentoInicial).toLocaleDateString("pt-BR") : "Não informada"}
            </MDTypography>
            <MDTypography>Juros Mensal: {taxaJurosNum}%</MDTypography>
            <MDTypography>Juros Totais a Pagar: {formatCurrency(jurosTotal)}</MDTypography>
            <MDTypography>Situação: {situacao}</MDTypography>
            <MDTypography>Observações: {observacoes || "Nenhuma"}</MDTypography>
          </MDBox>
        );
      default:
        return null;
    }
  };

  const getEditStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Nome da Dívida"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              fullWidth
              required
            />
            <Select value={tipo} onChange={(e) => setTipo(e.target.value)} fullWidth displayEmpty required>
              <MenuItem value="">Selecione o tipo</MenuItem>
              <MenuItem value="Cartão de Crédito">Cartão de Crédito</MenuItem>
              <MenuItem value="Financiamento">Financiamento</MenuItem>
              <MenuItem value="Empréstimo">Empréstimo</MenuItem>
              <MenuItem value="Outros">Outros</MenuItem>
            </Select>
            <TextField
              label="Valor Total (R$)"
              value={formatCurrency(valorTotalNum)}
              onChange={(e) => handleCurrencyInputChange(e, setValorTotalNum)}
              fullWidth
              required
              placeholder="R$ 0,00"
              InputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
            <TextField
              label="Número de Parcelas"
              value={parcelas}
              onChange={(e) => setParcelas(e.target.value)}
              fullWidth
              required
              type="number"
            />
          </MDBox>
        );
      case 1:
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Data de Vencimento da Primeira Parcela"
              type="date"
              value={vencimentoInicial}
              onChange={(e) => setVencimentoInicial(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { height: "56px" } }}
            />
            <TextField
              label="Juros Mensal (%)"
              value={taxaJuros}
              onChange={(e) => setTaxaJuros(e.target.value)}
              fullWidth
              type="number"
              InputProps={{ inputProps: { step: 0.1, min: 0 } }}
            />
            <Select value={situacao} onChange={(e) => setSituacao(e.target.value)} fullWidth required>
              <MenuItem value="Em andamento">Em andamento</MenuItem>
              <MenuItem value="Quitada">Quitada</MenuItem>
            </Select>
            <TextField
              label="Observações"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </MDBox>
        );
      case 2:
        const valorTotalNumParsed = parseFloat(valorTotalNum) || 0;
        const taxaJurosNum = parseFloat(taxaJuros) || 0;
        const parcelasNum = parseInt(parcelas) || 1;
        const { jurosTotal, valorParcela } = calculateAmortization(valorTotalNumParsed, taxaJurosNum, parcelasNum);

        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography variant="h6">Revise os Dados:</MDTypography>
            <MDTypography>Nome: {descricao || "Não informada"}</MDTypography>
            <MDTypography>Tipo: {tipo || "Não informado"}</MDTypography>
            <MDTypography>Valor Total: {formatCurrency(valorTotalNumParsed)}</MDTypography>
            <MDTypography>Número de Parcelas: {parcelasNum}</MDTypography>
            <MDTypography>Valor da Parcela: {formatCurrency(valorParcela)}</MDTypography>
            <MDTypography>
              Data de Vencimento:{" "}
              {vencimentoInicial ? new Date(vencimentoInicial).toLocaleDateString("pt-BR") : "Não informada"}
            </MDTypography>
            <MDTypography>Juros Mensal: {taxaJurosNum}%</MDTypography>
            <MDTypography>Juros Totais a Pagar: {formatCurrency(jurosTotal)}</MDTypography>
            <MDTypography>Situação: {situacao}</MDTypography>
            <MDTypography>Observações: {observacoes || "Nenhuma"}</MDTypography>
          </MDBox>
        );
      default:
        return null;
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && (!descricao || !tipo || !valorTotalNum || !parcelas)) {
      setError("Preencha todos os campos obrigatórios do Passo 1.");
      return;
    }
    if (activeStep === 1 && (!vencimentoInicial || !situacao)) {
      setError("Preencha todos os campos obrigatórios do Passo 2.");
      return;
    }
    if (activeStep === steps.length - 1) handleCadastrarDivida();
    else {
      setActiveStep((prev) => prev + 1);
      setError("");
    }
  };

  const handleEditNext = () => {
    if (editActiveStep === 0 && (!descricao || !tipo || !valorTotalNum || !parcelas)) {
      setError("Preencha todos os campos obrigatórios do Passo 1.");
      return;
    }
    if (editActiveStep === 1 && (!vencimentoInicial || !situacao)) {
      setError("Preencha todos os campos obrigatórios do Passo 2.");
      return;
    }
    if (editActiveStep === steps.length - 1) {
      handleEditarDivida(selectedDividaId, {
        descricao,
        tipo,
        valorTotal: parseFloat(valorTotalNum) || 0,
        parcelas: parseInt(parcelas) || 1,
        vencimentoInicial: new Date(vencimentoInicial).toISOString(),
        taxaJuros: parseFloat(taxaJuros) || 0,
        situacao,
        observacoes,
      });
    } else {
      setEditActiveStep((prev) => prev + 1);
      setError("");
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError("");
  };

  const handleEditBack = () => {
    setEditActiveStep((prev) => prev - 1);
    setError("");
  };

  const handleEditOpen = (divida) => {
    setSelectedDividaId(divida.id);
    setDescricao(divida.descricao);
    setTipo(divida.tipo);
    setValorTotalNum(divida.valorTotal.toString());
    setParcelas(divida.parcelas.toString());
    setVencimentoInicial(new Date(divida.vencimentoInicial).toISOString().split("T")[0]);
    setTaxaJuros(divida.taxaJuros ? divida.taxaJuros.toString() : "");
    setSituacao(divida.situacao);
    setObservacoes(divida.observacoes || "");
    setOpenEditModal(true);
  };

  const handleDeleteOpen = (id) => {
    setSelectedDividaId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    handleExcluirDivida(selectedDividaId);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteConfirm(false);
    setSelectedDividaId(null);
  };

  const filteredDividas = dividas.filter((divida) => {
    const matchesSearch = divida.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      !filterStatus ||
      (filterStatus === "Atrasado" &&
        new Date(divida.vencimentoInicial) < new Date() &&
        divida.situacao !== "Quitada") ||
      (filterStatus === "A vencer" &&
        new Date(divida.vencimentoInicial) >= new Date() &&
        divida.situacao !== "Quitada") ||
      (filterStatus === "Quitada" && divida.situacao === "Quitada");
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case "Atrasado":
        return "🔴";
      case "Em andamento":
      case "Pendente":
        return "🟡";
      case "Quitada":
        return "🟢";
      default:
        return "🟡";
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab label="Gerenciar" />
          <Tab label="Resumo" />
        </Tabs>
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1, border: 1, borderColor: darkMode ? "#444" : "#e0e0e0" }}>
          {tabValue === 0 && (
            <MDBox>
              {/* Lista de Dívidas */}
              <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" color="text.primary">
                  Lista de Dívidas
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                >
                  Adicionar Dívida
                </MDButton>
              </MDBox>
              <MDBox mb={2} display="flex" gap={2}>
                <TextField
                  label="Pesquisar por Nome"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  sx={{ maxWidth: "300px" }}
                />
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  fullWidth
                  sx={{ maxWidth: "200px" }}
                  displayEmpty
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Atrasado">Atrasado</MenuItem>
                  <MenuItem value="A vencer">A vencer</MenuItem>
                  <MenuItem value="Quitada">Quitada</MenuItem>
                </Select>
              </MDBox>
              {isLoading ? (
                <MDTypography textAlign="center">Carregando...</MDTypography>
              ) : dividas.length === 0 ? (
                <MDTypography textAlign="center" color={darkMode ? "white" : "text.secondary"}>
                  Nenhuma dívida cadastrada.
                </MDTypography>
              ) : filteredDividas.length === 0 ? (
                <MDTypography textAlign="center" color={darkMode ? "white" : "text.secondary"}>
                  Nenhuma dívida corresponde aos filtros selecionados.
                </MDTypography>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 0, border: "none" }}>
                  <Table sx={{ minWidth: 650, borderCollapse: "separate", borderSpacing: 0 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: darkMode ? "#2E3B4E" : "#e3f2fd",
                          "& th": {
                            color: darkMode ? "white" : "#4a90e2",
                            fontWeight: "bold",
                            textAlign: "center",
                            borderBottom: "1px solid #e0e0e0",
                            padding: "8px",
                            whiteSpace: "nowrap",
                          },
                        }}
                      >
                        <TableCell sx={{ width: "25%", padding: "8px" }}>Nome</TableCell>
                        <TableCell sx={{ width: "15%", padding: "8px" }}>Tipo</TableCell>
                        <TableCell sx={{ width: "15%", padding: "8px" }}>Valor Total</TableCell>
                        <TableCell sx={{ width: "15%", padding: "8px" }}>Juros Totais</TableCell>
                        <TableCell sx={{ width: "10%", padding: "8px" }}>Parcelas</TableCell>
                        <TableCell sx={{ width: "10%", padding: "8px" }}>Vencimento</TableCell>
                        <TableCell sx={{ width: "10%", padding: "8px" }}>Status</TableCell>
                        <TableCell sx={{ width: "10%", padding: "8px" }}>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDividas.map((divida) => {
                        const status =
                          new Date(divida.vencimentoInicial) < new Date() && divida.situacao !== "Quitada"
                            ? "Atrasado"
                            : divida.situacao === "Quitada"
                            ? "Quitada"
                            : "Em andamento";
                        const parcelasPagas = divida.parcelasPagas || 0;
                        const totalParcelas = divida.parcelas || 1;

                        return (
                          <TableRow
                            key={divida.id}
                            sx={{
                              "&:nth-of-type(even)": { backgroundColor: darkMode ? "#2E3B4E" : "#f9f9f9" },
                              "&:hover": { backgroundColor: darkMode ? "#40495F" : "#f0f0f0" },
                              "& td": {
                                borderBottom: "1px solid #e0e0e0",
                                padding: "8px",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                              },
                            }}
                          >
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {divida.descricao}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {divida.tipo}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {formatCurrency(divida.valorTotal)}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {formatCurrency(divida.jurosTotalEstimado || 0)}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {`${parcelasPagas}/${totalParcelas}`}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {new Date(divida.vencimentoInicial).toLocaleDateString("pt-BR")}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="body2" color={darkMode ? "white" : "text.primary"}>
                                {getStatusIcon(status)} {status}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDBox display="flex" justifyContent="center" gap={1}>
                                <IconButton color="warning" onClick={() => handleEditOpen(divida)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton color="error" onClick={() => handleDeleteOpen(divida.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </MDBox>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {error && (
                <MDTypography color="error" mt={2} textAlign="center">
                  {error}
                </MDTypography>
              )}
            </MDBox>
          )}

          {tabValue === 1 && (
            <MDBox>
              <MDTypography variant="h6" mb={2} color="text.primary">
                Resumo
              </MDTypography>
              <MDTypography>Em desenvolvimento...</MDTypography>
            </MDBox>
          )}
        </Card>

        {/* Modal de Cadastro */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Adicionar Nova Dívida
            <IconButton
              aria-label="close"
              onClick={() => setOpenModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {getStepContent(activeStep)}
            <MDBox sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <MDButton variant="outlined" color="secondary" onClick={handleBack} disabled={activeStep === 0}>
                Anterior
              </MDButton>
              <MDButton variant="contained" color="primary" onClick={handleNext} disabled={isLoading}>
                {activeStep === steps.length - 1 ? (isLoading ? "Carregando..." : "Finalizar") : "Próximo"}
              </MDButton>
            </MDBox>
            {error && (
              <MDTypography color="error" mt={2} textAlign="center">
                {error}
              </MDTypography>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Edição */}
        <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Editar Dívida
            <IconButton
              aria-label="close"
              onClick={() => setOpenEditModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stepper activeStep={editActiveStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {getEditStepContent(editActiveStep)}
            <MDBox sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <MDButton variant="outlined" color="secondary" onClick={handleEditBack} disabled={editActiveStep === 0}>
                Anterior
              </MDButton>
              <MDButton variant="contained" color="primary" onClick={handleEditNext} disabled={isLoading}>
                {editActiveStep === steps.length - 1 ? (isLoading ? "Carregando..." : "Salvar") : "Próximo"}
              </MDButton>
            </MDBox>
            {error && (
              <MDTypography color="error" mt={2} textAlign="center">
                {error}
              </MDTypography>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Confirmação de Exclusão */}
        <Dialog open={openDeleteConfirm} onClose={handleDeleteCancel}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <MDTypography>Tem certeza de que deseja excluir esta dívida?</MDTypography>
            <MDBox sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <MDButton variant="outlined" color="secondary" onClick={handleDeleteCancel}>
                Não
              </MDButton>
              <MDButton variant="contained" color="error" onClick={handleDeleteConfirm} disabled={isLoading}>
                {isLoading ? "Carregando..." : "Sim"}
              </MDButton>
            </MDBox>
          </DialogContent>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default DividasPage;
